// Phone status pill (SPEC §15.1): Device state, microphone permission, call-quality warnings. Click for the audio
// device picker, a microphone test and Retry.
import { useEffect, useRef, useState } from 'preact/hooks';
import { Dot } from '../components/ui-extra';
import { useMe } from '../me';
import { phone, type PhoneState } from './manager';
import { usePhone } from './PhoneProvider';

function summary(s: PhoneState): { tone: 'ok' | 'warn' | 'bad' | 'info' | 'idle'; text: string } {
  if (s.status === 'unsupported') return { tone: 'bad', text: "Browser can't call" };
  if (s.status === 'not_configured') return { tone: 'bad', text: 'Calling not set up' };
  if (s.mic === 'denied') return { tone: 'bad', text: 'Microphone blocked' };
  if (s.status === 'error') return { tone: 'bad', text: 'Phone error' };
  if (s.active?.reconnecting) return { tone: 'warn', text: 'Reconnecting call…' };
  if (s.warnings.length) return { tone: 'warn', text: s.warnings[0] };
  if (s.status === 'offline') return { tone: 'warn', text: 'Phone reconnecting' };
  if (s.status === 'starting' || s.status === 'registering') return { tone: 'info', text: 'Phone connecting…' };
  if (s.status === 'off') return { tone: 'idle', text: 'Phone off' };
  if (s.active) return { tone: 'ok', text: s.active.muted ? 'On call · muted' : 'On call' };
  return { tone: 'ok', text: s.mic === 'granted' ? 'Phone ready' : 'Phone ready · mic not tested' };
}

export function PhoneStatusPill() {
  const s = usePhone();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); window.removeEventListener('keydown', onKey); };
  }, [open]);
  const sum = summary(s);
  const canRetry = !s.active && (s.status === 'error' || s.status === 'offline' || s.status === 'off' || s.status === 'not_configured');
  return (
    <div class="vd-pill-wrap" ref={ref}>
      <button class={`vd-pill vd-pill-${sum.tone}`} onClick={() => setOpen(!open)} aria-expanded={open} title="Phone status and audio devices">
        <Dot tone={sum.tone} /> {sum.text}
      </button>
      {open ? (
        <div class="vd-popover card">
          <div class="eyebrow">Phone</div>
          {s.message ? <p class="vd-pop-msg">{s.message}</p> : <p class="vd-pop-msg muted">{sum.text}.</p>}
          {s.detail && me.role === 'admin' ? <p class="vd-field-hint">Admin detail: {s.detail}</p> : null}
          {s.warnings.length > 1 ? <ul class="vd-pop-list">{s.warnings.map((w) => <li>{w}</li>)}</ul> : null}
          {canRetry ? <button class="btn btn-sm" onClick={() => void phone.restart()}>Retry phone connection</button> : null}

          <div class="eyebrow" style="margin-top:12px">Microphone</div>
          {s.mic === 'denied'
            ? <p class="vd-pop-msg">Blocked. Click the icon at the left of the address bar, allow the microphone, then reload.</p>
            : null}
          <div class="row" style="gap:6px;flex-wrap:wrap">
            <select aria-label="Microphone" value={s.inputId ?? ''} disabled={!s.inputs.length || !!s.active}
              onChange={(e) => void phone.setInput((e.currentTarget as HTMLSelectElement).value)} style="flex:1;min-width:0">
              {s.inputs.length === 0 ? <option value="">No microphones listed yet</option> : null}
              {s.inputs.map((d) => <option value={d.id}>{d.label}</option>)}
            </select>
            <button class="btn btn-sm" onClick={() => void phone.requestMic()}>{s.mic === 'granted' ? 'Re-test' : 'Test microphone'}</button>
          </div>
          {s.active ? <p class="vd-field-hint">Switch devices between calls.</p> : null}

          <div class="eyebrow" style="margin-top:12px">Speaker</div>
          {s.outputSelectable ? (
            <div class="row" style="gap:6px">
              <select aria-label="Speaker" value={s.outputId ?? ''} onChange={(e) => void phone.setOutput((e.currentTarget as HTMLSelectElement).value)} style="flex:1;min-width:0">
                {s.outputs.map((d) => <option value={d.id}>{d.label}</option>)}
              </select>
              <button class="btn btn-sm" onClick={() => void phone.testSpeaker()}>Play test</button>
            </div>
          ) : <p class="vd-field-hint">This browser plays calls through the system default speaker. Change it in your computer's sound settings.</p>}
        </div>
      ) : null}
    </div>
  );
}
